import React from "react"
import { makeStyles } from "@material-ui/core/styles"
import { Delete as DeleteIcon } from "@material-ui/icons"
import Table from "@material-ui/core/Table"
import TableBody from "@material-ui/core/TableBody"
import TableCell from "@material-ui/core/TableCell"
import TableContainer from "@material-ui/core/TableContainer"
import TableHead from "@material-ui/core/TableHead"
import TableRow from "@material-ui/core/TableRow"

import {
  AssociatedIdentifier,
  AssociatedIdentifierObject,
} from "../store/types"
import {
  Dialog,
  IconButton,
  List,
  ListItemText,
  DialogTitle,
  ListItem,
} from "@material-ui/core"

const useStyles = makeStyles({
  table: {
    minWidth: 300,
  },
})

export interface Props {
  associatedIdentifiers: AssociatedIdentifier[]
  onRemoveAssociatedIdentifier(identifier: string): void
}

const AssociatedIdentifiersTable: React.FC<Props> = ({
  associatedIdentifiers,
  onRemoveAssociatedIdentifier,
}) => {
  const classes = useStyles()

  const [identifiers, setIdentifiers] = React.useState<
    AssociatedIdentifierObject[]
  >([])
  const [modalOpen, setModalOpen] = React.useState<boolean>(false)
  const [selectedKey, setSelectedKey] = React.useState<string>("")

  React.useEffect(() => {
    // turn the associatedIdentifiers into an array of objects
    const objs = associatedIdentifiers.map((identifier) => {
      const [key, value] = Object.entries(identifier)[0] || ["", ""]
      return { key, identifier: value }
    })
    setIdentifiers(objs)
  }, [associatedIdentifiers])

  const handleOpenModal = React.useCallback((key: string) => {
    setSelectedKey(key)
    setModalOpen(true)
  }, [])

  const handleCloseModal = React.useCallback(() => {
    setModalOpen(false)
  }, [])

  const handleConfirmRemove = React.useCallback(() => {
    onRemoveAssociatedIdentifier(selectedKey)
    setModalOpen(false)
  }, [onRemoveAssociatedIdentifier, selectedKey])

  return (
    <>
      <TableContainer>
        <Table className={classes.table} aria-label="Associated Identifiers">
          <TableHead>
            <TableRow>
              <TableCell>Key</TableCell>
              <TableCell>Identifier</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {identifiers.length > 0 ? (
              identifiers.map((identifier) => (
                <TableRow
                  key={`tag-row-identifier-${identifier.key}-${identifier.identifier}`}
                  data-qa-selector={`tag-row-${identifier.key}-${identifier.identifier}`}
                >
                  <TableCell
                    component="th"
                    scope="row"
                    key={`tag-cell-key-${identifier.key}`}
                  >
                    {identifier.key}
                  </TableCell>
                  <TableCell key={`tag-cell-value-${identifier.identifier}`}>
                    {identifier.identifier}
                  </TableCell>
                  <TableCell
                    align="right"
                    padding="none"
                    size="small"
                    key={`tag-cell-delete-${identifier.key}-${identifier.identifier}`}
                  >
                    <IconButton
                      onClick={() => handleOpenModal(identifier.key || "")}
                      data-qa-selector="delete"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={3}>No associated identifiers</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        className="confirmation"
      >
        <DialogTitle>
          Are you sure you want to remove this identifier?
        </DialogTitle>
        <List>
          <ListItem autoFocus button onClick={handleConfirmRemove}>
            <ListItemText primary="Yes, remove identifier" />
          </ListItem>
          <ListItem autoFocus button onClick={handleCloseModal}>
            <ListItemText primary="No, cancel" />
          </ListItem>
        </List>
      </Dialog>
    </>
  )
}

export default AssociatedIdentifiersTable
